import {
  AccountTypes,
  IndividualDesignationListedTypes,
  IRADesignationListedTypes,
} from '@interfaces/models';
import { JOINT_ACCOUNTS_TYPES } from '@interfaces/state';
import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { TaxClassification } from '@src/app/secure/terms/components/backup-withholding/backup-withholding.component';
import * as AdaptedStateActions from '@store/actions/adapted-state/adapted-state.actions';
import { StartOverStateCleared } from '@store/actions/start-over/start-over.actions';
import { AdaptedState, TransactionState } from '@store/index';

const INDIVIDUAL_OR_JOINT_TYPES = [AccountTypes.INDV, ...JOINT_ACCOUNTS_TYPES];

export function getTransactionsForTaxClassification(
  transactions: TransactionState[],
  taxClassification: TaxClassification,
): TransactionState[] {
  switch (taxClassification) {
    case TaxClassification.IRA:
      return transactions.filter(
        (t) =>
          t.accountTriage.accountTypeInheriting === AccountTypes.IRA &&
          t.accountTriage.IRADesignation !== IRADesignationListedTypes.EXECUTOR &&
          t.accountTriage.IRADesignation !== IRADesignationListedTypes.TRUSTEE,
      );
    case TaxClassification.Individual:
      return transactions.filter(
        (t) =>
          INDIVIDUAL_OR_JOINT_TYPES.includes(t.accountTriage.accountTypeInheriting) &&
          t.accountTriage.individualDesignation !== IndividualDesignationListedTypes.EXECUTOR &&
          t.accountTriage.individualDesignation !== IndividualDesignationListedTypes.TRUSTEE,
      );
    case TaxClassification.TRUST:
      return transactions.filter(
        (t) =>
          (t.accountTriage.accountTypeInheriting === AccountTypes.IRA &&
            t.accountTriage.IRADesignation === IRADesignationListedTypes.TRUSTEE) ||
          ([AccountTypes.INDV, AccountTypes.JOINT].includes(
            t.accountTriage.accountTypeInheriting,
          ) &&
            t.accountTriage.individualDesignation === IndividualDesignationListedTypes.TRUSTEE),
      );
    case TaxClassification.ESTATE:
      return transactions.filter(
        (t) =>
          (t.accountTriage.accountTypeInheriting === AccountTypes.IRA &&
            t.accountTriage.IRADesignation === IRADesignationListedTypes.EXECUTOR) ||
          (INDIVIDUAL_OR_JOINT_TYPES.includes(t.accountTriage.accountTypeInheriting) &&
            t.accountTriage.individualDesignation === IndividualDesignationListedTypes.EXECUTOR),
      );
    default:
      return [];
  }
}

export const transactionsAdapter: EntityAdapter<TransactionState> =
  createEntityAdapter<TransactionState>({
    selectId: (state: TransactionState) => state.currentTransactionId,
  });

export const initialState: AdaptedState = transactionsAdapter.getInitialState({});

export const adaptedStateReducer = createReducer(
  initialState,
  on(AdaptedStateActions.AddStateToTransactions, (adaptedState, { state }) =>
    transactionsAdapter.addOne(state, adaptedState),
  ),
  on(AdaptedStateActions.UpdateTransactionState, (adaptedState, { update }) =>
    transactionsAdapter.updateOne(update, adaptedState),
  ),
  on(AdaptedStateActions.DeleteTransactionState, (state, { id }) =>
    transactionsAdapter.removeOne(id, state),
  ),
  on(AdaptedStateActions.LoadTransactionStates, (state, { states }) =>
    transactionsAdapter.setAll(states, state),
  ),
  on(AdaptedStateActions.ClearTransactionStates, (state) =>
    transactionsAdapter.removeAll({ ...state, currentTransactionId: null }),
  ),
  on(
    AdaptedStateActions.StampDraftOnTransaction,
    (state, { transactionId, draftId, draftSequence }) =>
      transactionsAdapter.updateOne(
        { id: transactionId, changes: { draftId, draftSequence } },
        state,
      ),
  ),
  on(
    AdaptedStateActions.SetBackupWithholdingSignatureName,
    (state, { taxClassification, signatureName, trustName, ein, grantorSsn, estateName, tin }) => {
      const allTransactions = transactionsAdapter.getSelectors().selectAll(state);
      let filtered = getTransactionsForTaxClassification(allTransactions, taxClassification);
      if (taxClassification === TaxClassification.TRUST && trustName) {
        filtered = filtered.filter(
          (transaction) =>
            transaction.trust?.trustName === trustName &&
            (ein
              ? transaction.trustTaxInfo?.ein === ein
              : transaction.trustTaxInfo?.ssn === grantorSsn),
        );
      }
      if (taxClassification === TaxClassification.ESTATE && estateName) {
        filtered = filtered.filter(
          (transaction) =>
            transaction.estate?.estateName === estateName &&
            transaction.estate?.tin_private === tin,
        );
      }
      const updates = filtered.map((transaction) => ({
        id: transaction.currentTransactionId,
        changes: { backupWithholdingSignatureName: signatureName },
      }));
      return transactionsAdapter.updateMany(updates, state);
    },
  ),
  on(
    AdaptedStateActions.SetBackupWithholdingAnswer,
    (state, { taxClassification, withholdingAnswer }) => {
      const allTransactions = transactionsAdapter.getSelectors().selectAll(state);
      const updates = getTransactionsForTaxClassification(allTransactions, taxClassification).map(
        (transaction) => ({
          id: transaction.currentTransactionId,
          changes: { backupWithholdingAnswer: withholdingAnswer },
        }),
      );
      return transactionsAdapter.updateMany(updates, state);
    },
  ),
  on(StartOverStateCleared, () => initialState),
);

export const transactionIdReducer = createReducer(
  '',
  on(AdaptedStateActions.SetTransactionId, (state, { id }) => id),
  on(AdaptedStateActions.ClearCurrentTransactionState, () => ''),
  on(StartOverStateCleared, () => ''),
  on(
    AdaptedStateActions.SetTransactionAsCurrent,
    (state, { transaction }) => transaction.currentTransactionId,
  ),
);

export const transactionsReviewEditModeReducer = createReducer(
  false,
  on(
    AdaptedStateActions.SetTransactionReviewPageEditMode,
    (state, { isReviewPageReachedAndEditMode }) => isReviewPageReachedAndEditMode,
  ),
  on(StartOverStateCleared, () => false),
);

export const transactionsOAReviewEditModeReducer = createReducer(
  false,
  on(
    AdaptedStateActions.SetTransactionOAReviewPageEditMode,
    (state, { isOAReviewPageReachedAndEditMode }) => isOAReviewPageReachedAndEditMode,
  ),
  on(StartOverStateCleared, () => false),
);

export const transactionInvestmentObjectivesEditModeReducer = createReducer(
  false,
  on(
    AdaptedStateActions.SetTransactionReviewInvestmentObjectivesMode,
    (state, { isMultiReviewPageReachedAndSkipInvestmentRevision }) =>
      isMultiReviewPageReachedAndSkipInvestmentRevision,
  ),
  on(StartOverStateCleared, () => false),
);
